import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 16 — Coach shell, Coach Today and CoachTask.
 * Mirrors reviewQueries.js's `request()` error contract exactly.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });
const jsonPut = (path, payload) => request(path, { method: "PUT", body: JSON.stringify(payload ?? {}) });
export { request, jsonPost, jsonPut };
const retryUnless4xx = (failureCount, error) => (error?.status ? error.status >= 500 && failureCount < 2 : failureCount < 2);

export function useCoachToday() {
  return useQuery({
    queryKey: ["coach-today"],
    queryFn: () => request(`/api/rehearsal-room/coach/today`),
    retry: retryUnless4xx,
    refetchInterval: 60000,
  });
}

export function useCoachSearch(query) {
  return useQuery({
    queryKey: ["coach-search", query],
    queryFn: () => request(`/api/rehearsal-room/coach/search?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length >= 2,
    retry: retryUnless4xx,
  });
}

export function useCoachNotifications() {
  return useQuery({
    queryKey: ["coach-notifications"],
    queryFn: () => request(`/api/rehearsal-room/coach/notifications`),
    retry: retryUnless4xx,
    refetchInterval: 60000,
  });
}

export function useMarkNotificationOpen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, ntfId }) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/notifications/${ntfId}/open`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-notifications"] }),
  });
}

export function useCoachClients(filter = "all") {
  return useQuery({
    queryKey: ["coach-clients", filter],
    queryFn: () => request(`/api/rehearsal-room/coach/clients?filter=${filter}`),
    retry: retryUnless4xx,
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/coach/workspaces`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-clients"] }),
  });
}

// -- Master v5 Prompt 30 — one-step "Invite new client" (Resend email) -----
export function useInviteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/coach/invite-client`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coach-clients"] });
      qc.invalidateQueries({ queryKey: ["coach-pending-invites"] });
    },
  });
}

export function usePendingInvites() {
  return useQuery({
    queryKey: ["coach-pending-invites"],
    queryFn: () => request(`/api/rehearsal-room/coach/pending-invites`),
    retry: retryUnless4xx,
  });
}

export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId) => jsonPost(`/api/rehearsal-room/coach/invites/${inviteId}/resend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-pending-invites"] }),
  });
}

export function useCancelInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId) => jsonPost(`/api/rehearsal-room/coach/invites/${inviteId}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-pending-invites"] }),
  });
}

// -- Master v5 Prompt 17 — workspace lifecycle + Previous Programmes -------
export function useSetWorkspaceStatus(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/status`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-clients"] }),
  });
}

export function usePreviousProgrammes(workspaceId, targetMembershipId) {
  return useQuery({
    queryKey: ["previous-programmes", workspaceId, targetMembershipId],
    enabled: !!workspaceId && !!targetMembershipId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/previous-programmes?target_membership_id=${targetMembershipId}`),
  });
}

// Prompt 30D — "Remove access only" (mode="access_only", revokes the
// membership, keeps every session/file/feedback/document as-is) or
// "Delete everything" (mode="full", permanently cascades all of it) —
// see client_record_routes.delete_client_route.
export function useDeleteClient(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, mode, confirmName }) => request(`/api/rehearsal-room/workspaces/${workspaceId}/clients/${memberId}`, {
      method: "DELETE", body: JSON.stringify({ mode, confirm_name: confirmName }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-clients"] }),
  });
}

// Prompt 30D — permanently deletes an entire Engagement/Workspace
// (every member, session, file, presentation, feedback, message,
// contract/invoice tied to it) — see client_record_routes.delete_workspace_route.
export function useDeleteWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, confirmTitle }) => request(`/api/rehearsal-room/workspaces/${workspaceId}`, {
      method: "DELETE", body: JSON.stringify({ confirm_title: confirmTitle }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-clients"] }),
  });
}

// -- Master v5 Prompt 17 — View as Client -----------------------------------
export function useStartViewAs(workspaceId) {
  return useMutation({
    mutationFn: (targetMembershipId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/view-as-sessions`, { target_membership_id: targetMembershipId }),
  });
}

export function useEndViewAs(workspaceId) {
  return useMutation({
    mutationFn: (vasId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/view-as-sessions/${vasId}/end`),
  });
}

export function useActiveViewAs(workspaceId) {
  return useQuery({
    queryKey: ["view-as-active", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/view-as-sessions/active`),
    retry: false,
  });
}
